import { isDeceasedInSource, isPlaceholderEmail } from '@dosfilos/domain';

// ─── «Contactos específicos» de una campaña (puro, con test) ────────────────
// Antes, el selector cargaba `people` y `crmContacts` con `limit(500)` cada
// uno, y el VALOR del campo eran los ids guardados filtrados por esas listas.
// Con más de 500, un contacto elegido que quedaba fuera NO se veía, y al
// agregar o quitar cualquier otro, `contactIds` se reescribía con lo visible:
// el contacto se perdía sin que nadie lo hubiera quitado.
//
// INVARIANTE: guardar nunca quita contactos que la pantalla no llegó a cargar.
// Los elegidos se leen POR ID; el valor del campo son SIEMPRE todos los
// elegidos (con un marcador mientras alguno no carga); solo se quita uno si el
// usuario lo quita.

export interface ContactOption {
  id: string;
  name: string;
  email: string;
  source: 'SIS' | 'CRM';
  /** Elegido pero todavía no leído (o ya no existe): se conserva tal cual. */
  unresolved?: boolean;
}

/** Hasta cuántos por fuente se trae la lista completa, como siempre. */
export const CONTACT_LIST_CAP = 500;
/** Con más, se busca: como mucho esto por consulta. */
export const CONTACT_SEARCH_LIMIT = 20;

type Doc = { id: string; data: Record<string, unknown> };

/** `sis:<id>` o `crm:<id>`: el formato que ya guarda la campaña. */
export function contactKey(c: Pick<ContactOption, 'id' | 'source'>): string {
  return `${c.source === 'SIS' ? 'sis' : 'crm'}:${c.id}`;
}

export function parseContactKey(key: string): Pick<ContactOption, 'id' | 'source'> | null {
  const i = key.indexOf(':');
  if (i < 0) return null;
  const prefix = key.slice(0, i);
  const id = key.slice(i + 1);
  if (!id || (prefix !== 'sis' && prefix !== 'crm')) return null;
  return { id, source: prefix === 'sis' ? 'SIS' : 'CRM' };
}

export function personOption(d: Doc): ContactOption | null {
  if (d.data['deletedAt'] || !d.data['email']) return null;
  // Correo provisorio (`@no-email.invalid`): no es de nadie, no se ofrece. El
  // servidor tampoco le envía (AudienceResolver), aunque ya estuviera elegido.
  if (isPlaceholderEmail(d.data['email'] as string)) return null;
  // Fallecido en el origen (`deceased-in-source`): tampoco se ofrece, y el
  // servidor no le envía aunque ya estuviera elegido.
  if (isDeceasedInSource(d.data['tags'])) return null;
  return {
    id: d.id,
    name: `${(d.data['firstName'] as string) ?? ''} ${(d.data['lastName'] as string) ?? ''}`.trim(),
    email: (d.data['email'] as string).toLowerCase(),
    source: 'SIS',
  };
}

export function crmOption(d: Doc): ContactOption | null {
  if (d.data['deletedAt'] || !d.data['email']) return null;
  return {
    id: d.id,
    name: (d.data['name'] as string) ?? '',
    email: (d.data['email'] as string).toLowerCase(),
    source: 'CRM',
  };
}

/** La lista completa (seminario chico): misma deduplicación y orden que
 *  siempre. Un contacto CRM se omite si ya está vinculado a una persona SIS o
 *  si comparte correo con una. */
export function buildContactList(people: readonly Doc[], crm: readonly Doc[]): ContactOption[] {
  const sis = people.map(personOption).filter((o): o is ContactOption => !!o);
  const crmLinkedIds = new Set(people.map((d) => d.data['crmContactId']).filter(Boolean));
  const sisEmails = new Set(sis.map((p) => p.email));
  const crmOptions = crm
    .filter((d) => !crmLinkedIds.has(d.id))
    .map(crmOption)
    .filter((o): o is ContactOption => !!o && !sisEmails.has(o.email));
  return [...sis, ...crmOptions].sort(
    (a, b) => a.source.localeCompare(b.source) || a.name.localeCompare(b.name),
  );
}

/** El valor del campo: TODOS los elegidos, en su orden. Los que no se
 *  conocen todavía van como marcador, así un cambio no los borra. */
export function selectedContacts(
  contactIds: readonly string[],
  known: ReadonlyMap<string, ContactOption>,
): ContactOption[] {
  const out: ContactOption[] = [];
  for (const key of contactIds) {
    const hit = known.get(key);
    if (hit) {
      out.push(hit);
      continue;
    }
    const parsed = parseContactKey(key);
    if (parsed) out.push({ ...parsed, name: '', email: '', unresolved: true });
  }
  return out;
}

/** Lo que se guarda: las claves de lo que quedó en el campo. */
export function contactIdsOf(selected: readonly ContactOption[]): string[] {
  return [...new Set(selected.map(contactKey))];
}

/** Prefijos para buscar con más de CONTACT_LIST_CAP: el texto como viene, con
 *  mayúscula inicial y en minúsculas (Firestore distingue mayúsculas). */
export function prefixVariants(text: string): string[] {
  const t = text.trim();
  if (!t) return [];
  return [...new Set([t, t.charAt(0).toUpperCase() + t.slice(1).toLowerCase(), t.toLowerCase()])];
}

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Todas las palabras escritas aparecen en el nombre o el correo. */
export function matchesContact(o: ContactOption, text: string): boolean {
  const hay = norm(`${o.name} ${o.email}`);
  return norm(text)
    .split(/\s+/)
    .filter(Boolean)
    .every((w) => hay.includes(w));
}
